// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Teeter — the canvas renderer + juice. Draws the whole maze (fit, centred, no
// letterboxing) with light-steel walls on a dark floor, the pits (coral-rimmed),
// gems, the exit ring, the marble with a sheen and a fading motion trail, plus screen
// shake and particles fed from the game's event stream. Respects prefers-reduced-
// motion. No gameplay logic lives here — it reads GameState and paints.

import { cellCenter, solidAt, type Maze } from './maze';
import { HAZARD, type GameEvent, type GameState } from './game';
import { PALETTE } from './palette';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  col: string;
  r: number;
}

interface TrailDot {
  x: number;
  y: number;
  age: number;
}

export interface RenderOpts {
  dtMs: number;
  reducedMotion: boolean;
}

export class Board {
  private ctx: CanvasRenderingContext2D;
  private dpr = 1;
  private cw = 0;
  private ch = 0;
  private cell = 20;
  private ox = 0;
  private oy = 0;
  private maze: Maze | null = null;

  private particles: Particle[] = [];
  private trail: TrailDot[] = [];
  private shake = 0;
  private pulse = 0;

  constructor(private canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    this.ctx = ctx;
  }

  setMaze(maze: Maze): void {
    this.maze = maze;
    this.trail = [];
    this.particles = [];
    this.shake = 0;
    this.layout();
  }

  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) return; // ignore a transient 0-size measure
    this.dpr = Math.min(window.devicePixelRatio || 1, 2.5);
    this.cw = rect.width;
    this.ch = rect.height;
    this.canvas.width = Math.round(rect.width * this.dpr);
    this.canvas.height = Math.round(rect.height * this.dpr);
    this.layout();
  }

  private layout(): void {
    if (!this.maze || this.cw < 2) return;
    const margin = 10;
    const cellW = (this.cw - margin * 2) / this.maze.gw;
    const cellH = (this.ch - margin * 2) / this.maze.gh;
    this.cell = Math.max(2, Math.min(cellW, cellH));
    this.ox = (this.cw - this.cell * this.maze.gw) / 2;
    this.oy = (this.ch - this.cell * this.maze.gh) / 2;
  }

  private wx(x: number): number {
    return this.ox + x * this.cell;
  }
  private wy(y: number): number {
    return this.oy + y * this.cell;
  }

  /** Spawn shake + particles from this frame's events. */
  onEvents(events: GameEvent[], reduced: boolean): void {
    for (const e of events) {
      if (e.t === 'gem') {
        const g = this.maze?.gems[e.i];
        if (g) this.burst(cellCenter(g), PALETTE.gem, reduced ? 6 : 16, 3.5);
      } else if (e.t === 'fall') {
        if (!reduced) this.shake = Math.max(this.shake, 10);
      } else if (e.t === 'respawn') {
        const s = this.maze?.start;
        if (s && !reduced) this.burst(cellCenter(s), PALETTE.start, 10, 2.5);
      } else if (e.t === 'bump') {
        if (!reduced) this.shake = Math.max(this.shake, Math.min(4, e.speed * 0.7));
      } else if (e.t === 'win') {
        const ex = this.maze?.exit;
        if (ex) this.burst(cellCenter(ex), PALETTE.exit, reduced ? 8 : 40, 5);
        if (!reduced) this.shake = Math.max(this.shake, 6);
      }
    }
  }

  private burst(at: { x: number; y: number }, col: string, n: number, speed: number): void {
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + Math.random() * 0.5;
      const s = speed * (0.4 + Math.random() * 0.9);
      this.particles.push({
        x: at.x,
        y: at.y,
        vx: Math.cos(a) * s,
        vy: Math.sin(a) * s,
        life: 0,
        max: 0.4 + Math.random() * 0.5,
        col,
        r: 1.5 + Math.random() * 2,
      });
    }
  }

  frame(s: GameState, opts: RenderOpts): void {
    const ctx = this.ctx;
    const dt = Math.min(opts.dtMs, 60) / 1000;
    this.pulse += dt;
    if (!this.maze) this.setMaze(s.maze);

    // Update effects.
    if (this.shake > 0) this.shake = Math.max(0, this.shake - dt * 26);
    for (const p of this.particles) {
      p.life += dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vx *= 0.9;
      p.vy *= 0.9;
    }
    this.particles = this.particles.filter((p) => p.life < p.max);
    if (!opts.reducedMotion && !s.over) {
      this.trail.push({ x: s.ball.x, y: s.ball.y, age: 0 });
      for (const t of this.trail) t.age += dt;
      this.trail = this.trail.filter((t) => t.age < 0.35);
    }

    ctx.save();
    ctx.scale(this.dpr, this.dpr);
    ctx.clearRect(0, 0, this.cw, this.ch);
    if (this.shake > 0.2) {
      const a = this.pulse * 60;
      ctx.translate(Math.sin(a * 1.7) * this.shake * 0.6, Math.cos(a * 2.3) * this.shake * 0.6);
    }

    this.drawFloor(ctx);
    this.drawWalls(ctx);
    this.drawStart(ctx);
    this.drawHoles(ctx);
    this.drawExit(ctx, s.gemsLeft === 0);
    this.drawGems(ctx, s);
    this.drawTrail(ctx);
    this.drawMarble(ctx, s);
    this.drawParticles(ctx);
    ctx.restore();
  }

  private drawFloor(ctx: CanvasRenderingContext2D): void {
    const m = this.maze!;
    ctx.fillStyle = PALETTE.board;
    ctx.fillRect(this.wx(0), this.wy(0), m.gw * this.cell, m.gh * this.cell);
  }

  private drawWalls(ctx: CanvasRenderingContext2D): void {
    const m = this.maze!;
    ctx.fillStyle = PALETTE.wall;
    const c = this.cell;
    for (let gy = 0; gy < m.gh; gy++) {
      for (let gx = 0; gx < m.gw; gx++) {
        if (!solidAt(m, gx, gy)) continue;
        ctx.fillRect(this.wx(gx) - 0.4, this.wy(gy) - 0.4, c + 0.8, c + 0.8);
      }
    }
    // A subtle top sheen on the walls for depth (skip if cells are tiny).
    if (c > 8) {
      ctx.fillStyle = 'rgba(255,255,255,0.08)';
      for (let gy = 0; gy < m.gh; gy++) {
        for (let gx = 0; gx < m.gw; gx++) {
          if (!solidAt(m, gx, gy)) continue;
          if (!solidAt(m, gx, gy - 1)) ctx.fillRect(this.wx(gx), this.wy(gy), c, Math.max(1, c * 0.16));
        }
      }
    }
  }

  private drawStart(ctx: CanvasRenderingContext2D): void {
    const c = cellCenter(this.maze!.start);
    const r = this.cell * 0.34;
    ctx.fillStyle = 'rgba(127,212,255,0.16)';
    ctx.beginPath();
    ctx.arc(this.wx(c.x), this.wy(c.y), r * 1.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = PALETTE.start;
    ctx.lineWidth = Math.max(1, this.cell * 0.09);
    ctx.beginPath();
    ctx.arc(this.wx(c.x), this.wy(c.y), r, 0, Math.PI * 2);
    ctx.stroke();
  }

  private drawHoles(ctx: CanvasRenderingContext2D): void {
    const r = this.cell * HAZARD.HOLE_R;
    for (const h of this.maze!.holes) {
      const c = cellCenter(h);
      const px = this.wx(c.x);
      const py = this.wy(c.y);
      const grad = ctx.createRadialGradient(px, py, r * 0.2, px, py, r);
      grad.addColorStop(0, '#02040a');
      grad.addColorStop(1, PALETTE.hole);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(px, py, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = PALETTE.holeRim;
      ctx.lineWidth = Math.max(1.2, this.cell * 0.1);
      ctx.beginPath();
      ctx.arc(px, py, r, 0, Math.PI * 2);
      ctx.stroke();
    }
  }

  private drawExit(ctx: CanvasRenderingContext2D, open: boolean): void {
    const c = cellCenter(this.maze!.exit);
    const px = this.wx(c.x);
    const py = this.wy(c.y);
    const base = this.cell * 0.4;
    const pulse = 1 + Math.sin(this.pulse * 4) * 0.12;
    ctx.strokeStyle = open ? PALETTE.exit : 'rgba(229,111,208,0.35)';
    ctx.lineWidth = Math.max(1.5, this.cell * 0.12);
    for (let k = 0; k < (open ? 2 : 1); k++) {
      ctx.beginPath();
      ctx.arc(px, py, base * pulse * (1 - k * 0.35), 0, Math.PI * 2);
      ctx.stroke();
    }
    if (open) {
      ctx.fillStyle = 'rgba(229,111,208,0.22)';
      ctx.beginPath();
      ctx.arc(px, py, base * 0.5, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private drawGems(ctx: CanvasRenderingContext2D, s: GameState): void {
    const r = this.cell * 0.3;
    for (let i = 0; i < this.maze!.gems.length; i++) {
      if (s.collected[i]) continue;
      const c = cellCenter(this.maze!.gems[i]);
      const px = this.wx(c.x);
      const py = this.wy(c.y) + Math.sin(this.pulse * 3 + i) * this.cell * 0.05;
      ctx.save();
      ctx.translate(px, py);
      ctx.rotate(Math.PI / 4);
      ctx.fillStyle = PALETTE.gem;
      ctx.fillRect(-r * 0.72, -r * 0.72, r * 1.44, r * 1.44);
      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.fillRect(-r * 0.6, -r * 0.6, r * 0.5, r * 0.5);
      ctx.restore();
    }
  }

  private drawTrail(ctx: CanvasRenderingContext2D): void {
    for (const t of this.trail) {
      const a = (1 - t.age / 0.35) * 0.4;
      if (a <= 0) continue;
      ctx.fillStyle = `rgba(255,208,122,${a.toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(this.wx(t.x), this.wy(t.y), this.cell * HAZARD.BALL_R * 0.75, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private drawMarble(ctx: CanvasRenderingContext2D, s: GameState): void {
    const px = this.wx(s.ball.x);
    const py = this.wy(s.ball.y);
    const r = this.cell * HAZARD.BALL_R;
    // shadow
    ctx.fillStyle = 'rgba(0,0,0,0.35)';
    ctx.beginPath();
    ctx.arc(px + r * 0.18, py + r * 0.28, r * 0.98, 0, Math.PI * 2);
    ctx.fill();
    // body
    const grad = ctx.createRadialGradient(px - r * 0.35, py - r * 0.4, r * 0.15, px, py, r);
    grad.addColorStop(0, PALETTE.sheen);
    grad.addColorStop(0.5, PALETTE.marble);
    grad.addColorStop(1, '#c07d1c');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(px, py, r, 0, Math.PI * 2);
    ctx.fill();
    // glint
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    ctx.beginPath();
    ctx.arc(px - r * 0.34, py - r * 0.38, r * 0.22, 0, Math.PI * 2);
    ctx.fill();
  }

  private drawParticles(ctx: CanvasRenderingContext2D): void {
    for (const p of this.particles) {
      const a = 1 - p.life / p.max;
      ctx.globalAlpha = Math.max(0, a);
      ctx.fillStyle = p.col;
      ctx.beginPath();
      ctx.arc(this.wx(p.x), this.wy(p.y), p.r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  /** For the in-browser contrast pixel probe: pixel centre of a grid cell. */
  cellPixel(gx: number, gy: number): { x: number; y: number } {
    return { x: (this.wx(gx + 0.5)) * this.dpr, y: (this.wy(gy + 0.5)) * this.dpr };
  }

  destroy(): void {
    this.particles = [];
    this.trail = [];
  }
}
